/**
 * Leitura no banco do boleto de uma fatura, para as telas que mudam a etapa dela.
 *
 * A pergunta "esta fatura já tem boleto vivo?" é respondida pelo BANCO, nunca pela
 * fatura que a tela recebeu: o estado velho da tela é justamente o problema. Em
 * 16/10/2026 a Alice validou e calculou a fatura 14192 quatro minutos depois de o Ítalo
 * emitir o boleto 6816 — a tela dela era de antes da emissão — e a fatura voltou para
 * "Com boleto calculado" com o boleto vivo (ver emissaoBoletoValida.ts).
 *
 * A regra mora em emissaoBoletoValida.ts (sem I/O, com teste). Aqui fica só a leitura.
 * A barreira que também alcança o n8n e qualquer outro caminho é o trigger
 * `fn_barrar_volta_etapa_com_boleto_vivo`.
 */
import { supabase } from './supabase';
import {
    boletoJaRegistrado,
    motivoBoletoVivoNaoVoltaEtapa,
    type AcaoQueVoltaEtapa,
    type BoletoDaFatura,
} from './emissaoBoletoValida';

const COLUNAS = [
    'id', 'status_fatura', 'num_cobranca_boleto', 'linha_digitavel_boleto', 'codigo_barras_boleto_asaas',
    'url_boleto', 'empresa_parceira_id', 'valor_emissao_boleto_unico', 'valor_boleto_vigor',
    'vencimento_boleto', 'emitido_por_nome', 'emitido_em', 'ambiente',
].join(', ');

export type BoletoLido = BoletoDaFatura & { id: number | string };

/** Estado atual do boleto da fatura, direto do banco. Lança se a leitura falhar. */
export async function lerBoletoDaFatura(faturaId: number | string): Promise<BoletoLido> {
    const { data, error } = await supabase.from('faturas').select(COLUNAS).eq('id', faturaId).single();
    if (error || !data) {
        throw new Error(error?.message || 'fatura não encontrada');
    }
    return data as unknown as BoletoLido;
}

/**
 * Para a ação (validar, calcular, retroceder) se a fatura já tem boleto vivo.
 *
 * Falha de leitura também para: sem saber se há boleto, voltar a fatura de etapa pode
 * tirá-la da fila de envio. Fatura de teste (sandbox) passa, como no trigger.
 */
export async function barrarSeTemBoletoVivo(faturaId: number | string, acao: AcaoQueVoltaEtapa): Promise<void> {
    let atual: BoletoLido;
    try {
        atual = await lerBoletoDaFatura(faturaId);
    } catch (e: any) {
        throw new Error(
            'Não foi possível conferir se esta fatura já tem boleto emitido: '
            + (e?.message || 'erro desconhecido')
            + '. Nada foi alterado. Tente de novo em instantes.',
        );
    }
    if (atual.ambiente === 'sandbox') return;
    if (boletoJaRegistrado(atual)) {
        throw new Error(motivoBoletoVivoNaoVoltaEtapa(acao, atual));
    }
}
